import { useEffect, useState } from "react";
import type { Account } from "../lib/types";
import { api } from "../lib/api";

interface AssignmentCard {
  id: string;
  title: string;
  status: "ACTIVE" | "COMPLETED";
  learner: { displayName: string };
  version: number;
  updatedAt: string;
}

interface OwnExerciseCard {
  id: string;
  title: string;
  words: string[];
  archivedAt: string | null;
  updatedAt: string;
}

export function canLoadIndividualExerciseHome(account: Pick<Account, "roles" | "actingAs" | "modules">) {
  return account.modules.individualExercises
    && !account.roles.includes("ADMIN");
}

export function IndividualExerciseHome({ account }: { account: Account }) {
  const professional = account.roles.some((role) => role === "ORGANIZATION" || role === "THERAPIST");
  const canLoad = canLoadIndividualExerciseHome(account);
  const [exercises, setExercises] = useState<OwnExerciseCard[]>([]);
  const [assignments, setAssignments] = useState<AssignmentCard[]>([]);

  useEffect(() => {
    if (!canLoad) {
      setExercises([]);
      setAssignments([]);
      return;
    }
    if (professional) {
      Promise.all([
        api<{ mine: OwnExerciseCard[] }>("/learning/individual-exercises"),
        api<AssignmentCard[]>("/learning/individual-assignments"),
      ]).then(([library, rows]) => {
        setExercises(library.mine.filter(({ archivedAt }) => !archivedAt).slice(0, 3));
        setAssignments(rows.filter(({ status }) => status === "ACTIVE").slice(0, 3));
      }).catch(() => undefined);
    } else {
      api<AssignmentCard[]>("/learning/individual-assignments")
        .then((rows) => setAssignments(rows.filter(({ status }) => status === "ACTIVE").slice(0, 4)))
        .catch(() => undefined);
    }
  }, [account.id, account.actingAs?.id, canLoad]);

  if (!canLoad) return null;
  if (professional) return <section className="start-section individual-home-section">
    <div className="start-section-heading"><div><p className="eyebrow">Individuell begleiten</p><h2>Eigene Übungen</h2></div><button className="secondary small" onClick={() => { window.location.hash = "#/individual-exercises"; }}>Bibliothek öffnen</button></div>
    <div className="dashboard-grid">
      <div className="dashboard-card"><h3>Zuletzt bearbeitet</h3>{exercises.map((exercise) => <button className="content-link" key={exercise.id} onClick={() => { window.location.hash = "#/individual-exercises"; }}>{exercise.title}<small>{exercise.words.length} Wörter</small></button>)}{exercises.length === 0 && <p className="muted">Noch keine eigenen Übungen.</p>}</div>
      <div className="dashboard-card"><h3>Aktuelle Zuweisungen</h3>{assignments.map((assignment) => <button className="content-link" key={assignment.id} onClick={() => { window.location.hash = `#/individual-assignment/${encodeURIComponent(assignment.id)}`; }}>{assignment.title}<small>{assignment.learner.displayName} · Version {assignment.version}</small></button>)}{assignments.length === 0 && <p className="muted">Noch keine aktiven Zuweisungen.</p>}</div>
    </div>
  </section>;

  if (assignments.length === 0) return null;
  return <section className="start-section individual-home-section"><div className="start-section-heading"><div><p className="eyebrow">Für dich ausgewählt</p><h2>Deine individuellen Übungen</h2></div><button className="secondary small" onClick={() => { window.location.hash = "#/learning"; }}>Alle anzeigen</button></div><div className="learning-world-grid">{assignments.map((assignment) => <button className="learning-world-card world-2" key={assignment.id} onClick={() => { window.location.hash = `#/individual-assignment/${encodeURIComponent(assignment.id)}`; }}><span className="learning-world-icon" aria-hidden="true">⚡</span><span><strong>{assignment.title}</strong><small>Blitzlesen · Version {assignment.version}</small></span><b aria-hidden="true">→</b></button>)}</div></section>;
}
